import {useSettings} from '@ui/settings/use-settings';
import {useImmediateDomainCheck} from './use-immediate-domain-check';

/**
 * Check various conditions for menu items
 */
export function useMenuConditions() {
  const {base_url} = useSettings();
  const immediateCheck = useImmediateDomainCheck();
  
  const isMainDomain = (): boolean => {
    // Prefer app-domain meta tag (set from APP_DOMAIN); fallback to settings base_url; last resort immediate check
    const metaHost =
      typeof document !== 'undefined'
        ? document
            .querySelector('meta[name="app-domain"]')
            ?.getAttribute('content') || null
        : null;

    const currentHost = typeof window !== 'undefined' ? window.location.host : '';

    const settingsHost = (() => {
      if (!base_url) return null;
      try {
        return new URL(base_url).host;
      } catch (error) {
        console.warn('Settings URL parse error, falling back to immediate check:', error);
        return null;
      }
    })();

    // Prefer meta (APP_DOMAIN) as the true main domain; fall back to settingsHost, then immediate
    const mainHost = metaHost || settingsHost || immediateCheck.getMainDomain();

    const isMain =
      !!mainHost && (currentHost === mainHost || currentHost === `www.${mainHost}`);

    // Return the result - don't use fallback OR as it makes tenant domains return true
    return isMain;
  };
  
  const checkCondition = (condition: string): boolean => {
    switch (condition) {
      case 'main_domain_only':
        const mainDomain = isMainDomain();
        
        return mainDomain; // TRUE = show menu item, FALSE = hide menu item
      case 'admin':
        // Existing admin condition logic
        return true;
      default:
        return true;
    }
  };

  const shouldHideMenuItem = (item: any, menuPosition?: string): boolean => {
    // If this is the dedicated tenant admin sidebar, never hide tenant items
    if (menuPosition === 'tenant-admin-sidebar') {
      return false;
    }

    // Hide tenant-related items from NON-MAIN domains only (for central admin)
    const mainDomain = isMainDomain();
    if (!mainDomain) {
      const itemAction = item?.action || '';
      const itemLabel = (typeof item?.label === 'string'
        ? item.label
        : '')
        .toLowerCase();

      const isTenantItem =
        itemAction.includes('/admin/tenants') ||
        itemAction.includes('/tenants') ||
        itemAction === 'tenants' ||
        itemLabel.includes('tenant') ||
        itemLabel.includes('টেনান্ট');

      return isTenantItem;
    }

    return false;
  };

  return {
    checkCondition,
    isMainDomain,
    shouldHideMenuItem,
  };
}